import React from 'react';
import { X, Package } from 'lucide-react';

interface Client {
    id: number;
    company_name: string;
}

interface Consumable {
    id: number;
    title: string;
    base_price: number;
}

interface BpuCreationModalProps {
    isOpen: boolean;
    onClose: () => void;
    buyers: Client[];
    consumables: Consumable[];
    selectedConsumableIds: Set<number>;
    onConsumableToggle: (id: number) => void;
    onSelectAll: () => void;
    onDeselectAll: () => void;
    globalPricingMode: 'percent' | 'free_units';
    onModeSwitch: (mode: 'percent' | 'free_units') => void;
    globalApplyValue: string;
    onGlobalApplyValueChange: (value: string) => void;
    onApplyGlobalValue: () => void;
    consumablesSearch: string;
    onSearchChange: (value: string) => void;
    productPricing: Record<number, any>;
    onTierConfigOpen: (consumableId: number) => void;
    clientId: number | null;
    onClientChange: (clientId: number | null) => void;
    onSave: () => void;
}

const BpuCreationModal: React.FC<BpuCreationModalProps> = ({
    isOpen,
    onClose,
    buyers,
    consumables,
    selectedConsumableIds,
    onConsumableToggle,
    onSelectAll,
    onDeselectAll,
    globalPricingMode,
    onModeSwitch,
    globalApplyValue,
    onGlobalApplyValueChange,
    onApplyGlobalValue,
    consumablesSearch,
    onSearchChange,
    productPricing,
    onTierConfigOpen,
    clientId,
    onClientChange,
    onSave
}) => {
    if (!isOpen) return null;

    const getProductPricing = (consumableId: number) => {
        return productPricing[consumableId] || {
            custom_price: 0,
            discount_percent: 0,
            free_units: undefined,
            tiers: []
        };
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                            <Package className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Nouveau BPU</h2>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Client selection */}
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <span className="text-sm text-slate-500 dark:text-slate-400">Client</span>
                            <select
                                className="w-full mt-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                                value={clientId || ''}
                                onChange={(e) => onClientChange(e.target.value ? parseInt(e.target.value, 10) : null)}
                            >
                                <option value="">Sélectionner un client</option>
                                {buyers.map(client => (
                                    <option key={client.id} value={client.id}>{client.company_name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Products header */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                <Package className="h-5 w-5 text-emerald-600" />
                                Produits ({selectedConsumableIds.size} sélectionné{selectedConsumableIds.size > 1 ? 's' : ''})
                            </h3>
                            <div className="flex gap-2">
                                <button
                                    onClick={onSelectAll}
                                    className="text-xs text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 font-medium"
                                >
                                    Tout sélectionner
                                </button>
                                <span className="text-slate-300">|</span>
                                <button
                                    onClick={onDeselectAll}
                                    className="text-xs text-slate-600 hover:text-slate-700 dark:text-slate-400 font-medium"
                                >
                                    Tout désélectionner
                                </button>
                            </div>
                        </div>

                        {/* Mode selection */}
                        <div className="flex items-center gap-4 mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Mode de réduction :</span>
                            <div className="flex bg-slate-200 dark:bg-slate-700 rounded-lg p-1">
                                <button
                                    onClick={() => onModeSwitch('percent')}
                                    className={`px-6 py-2 text-sm font-medium rounded-md transition-all ${globalPricingMode === 'percent'
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                        }`}
                                >
                                    % Pourcentage
                                </button>
                                {false && (
                                    <button
                                        onClick={() => onModeSwitch('free_units')}
                                        className={`px-6 py-2 text-sm font-medium rounded-md transition-all ${globalPricingMode === 'free_units'
                                            ? 'bg-purple-600 text-white shadow-sm'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                            }`}
                                    >
                                        UG Unités Gratuites
                                    </button>
                                )}
                            </div>

                            {false && selectedConsumableIds.size > 0 && (
                                <div className="flex items-center gap-2 ml-auto">
                                    <span className="text-xs text-slate-500">Appliquer à tous sélectionnés :</span>
                                    <input
                                        type="text"
                                        placeholder={globalPricingMode === 'percent' ? '10' : '6'}
                                        className="w-20 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-right"
                                        value={globalApplyValue}
                                        onChange={(e) => onGlobalApplyValueChange(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                onApplyGlobalValue();
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={onApplyGlobalValue}
                                        disabled={!globalApplyValue.trim()}
                                        className="px-3 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-400"
                                    >
                                        Appliquer
                                    </button>
                                    <span className="text-sm text-slate-500">
                                        {globalPricingMode === 'percent' ? '%' : 'UG'}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Search */}
                        <div className="relative mb-4">
                            <input
                                type="text"
                                value={consumablesSearch}
                                onChange={(e) => onSearchChange(e.target.value)}
                                placeholder="Rechercher un produit..."
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                            />
                        </div>

                        {/* Products table */}
                        <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase w-16">Inclus</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Produit</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Prix Marché</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Paliers</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Prix Client</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                    {consumables
                                        .filter(c => c.title.toLowerCase().includes(consumablesSearch.toLowerCase()))
                                        .map((consumable) => {
                                            const isSelected = selectedConsumableIds.has(consumable.id);
                                            const pricing = getProductPricing(consumable.id);
                                            const basePrice = Number(consumable.base_price ?? 0);
                                            const customPrice = Number((pricing as any)?.custom_price);
                                            const hasCustomPrice = Number.isFinite(customPrice);

                                            return (
                                                <tr
                                                    key={consumable.id}
                                                    className={`transition-all ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'opacity-50 bg-slate-50 dark:bg-slate-800/30'}`}
                                                >
                                                    <td className="px-4 py-3 text-center">
                                                        <button
                                                            onClick={() => onConsumableToggle(consumable.id)}
                                                            className={`w-10 h-5 rounded-full transition-colors relative ${isSelected ? 'bg-emerald-500' : 'bg-slate-300'}`}
                                                        >
                                                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isSelected ? 'left-5' : 'left-0.5'}`} />
                                                        </button>
                                                    </td>
                                                    <td className={`px-4 py-3 text-sm ${isSelected ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400'}`}>
                                                        {consumable.title}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-right text-slate-600">
                                                        {basePrice.toFixed(2)} €
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {isSelected && (
                                                            <button
                                                                onClick={() => onTierConfigOpen(consumable.id)}
                                                                className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                                                            >
                                                                Configurer
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-right text-emerald-600 font-medium">
                                                        {isSelected && hasCustomPrice ? `${customPrice.toFixed(2)} €` : '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>

                        <div className="text-center text-sm text-slate-500 mt-4">
                            Total produits sélectionnés: <span className="font-bold text-emerald-600">{selectedConsumableIds.size}</span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 p-6 border-t border-slate-200 dark:border-slate-700">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                        Annuler
                    </button>
                    <button
                        onClick={onSave}
                        disabled={!clientId || selectedConsumableIds.size === 0}
                        className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-400 transition-colors font-medium"
                    >
                        Créer le BPU
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BpuCreationModal;
